import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

export type EvaluationScheduleStage = "notSetup" | "draft"

export interface EvaluationScheduleCourse {
  id: string
  courseCode: string
  title: string
  facultyName: string
  stage: EvaluationScheduleStage
}

const COURSE_POOL: Omit<EvaluationScheduleCourse, "id" | "stage">[] = [
  { courseCode: "OT-501", title: "Foundations of Occupation", facultyName: "Priya Shah" },
  { courseCode: "OT-512", title: "Occupational Assessment", facultyName: "Marcus Hale" },
  { courseCode: "OT-520", title: "Pediatric Practice", facultyName: "Elena Ruiz" },
  { courseCode: "OT-531", title: "Adult Rehabilitation", facultyName: "James Okonkwo" },
  { courseCode: "OT-540", title: "Mental Health Practice", facultyName: "Sofia Patel" },
  { courseCode: "OT-551", title: "Assistive Technology", facultyName: "Noah Berger" },
  { courseCode: "OT-562", title: "Community Based Practice", facultyName: "Aisha Rahman" },
  { courseCode: "OT-570", title: "Research Methods", facultyName: "Liam Chen" },
  { courseCode: "OT-581", title: "Fieldwork Seminar", facultyName: "Hannah Brooks" },
  { courseCode: "OT-590", title: "Professional Issues", facultyName: "Owen Park" },
]

const STAGE_LABEL: Record<EvaluationScheduleStage, string> = {
  notSetup: "Not set up",
  draft: "Draft",
}

export function parseScheduleStages(param: string | null): EvaluationScheduleStage[] {
  const allowed: EvaluationScheduleStage[] = ["notSetup", "draft"]
  const parsed = (param ?? "notSetup,draft")
    .split(",")
    .map(value => value.trim())
    .filter((value): value is EvaluationScheduleStage =>
      allowed.includes(value as EvaluationScheduleStage),
    )
  return parsed.length > 0 ? parsed : ["notSetup", "draft"]
}

export function scheduleStageLabel(stage: EvaluationScheduleStage): string {
  return STAGE_LABEL[stage]
}

export function scheduleTargetCount(
  term: EvaluationTerm,
  stages: EvaluationScheduleStage[],
): number {
  return stages.reduce((sum, stage) => sum + term.counts[stage], 0)
}

export function scheduleCoursesForTerm(
  term: EvaluationTerm,
  stages: EvaluationScheduleStage[],
): EvaluationScheduleCourse[] {
  const courses: EvaluationScheduleCourse[] = []
  let poolIndex = 0
  for (const stage of stages) {
    const count = term.counts[stage]
    for (let i = 0; i < count; i += 1) {
      const seed = COURSE_POOL[poolIndex % COURSE_POOL.length]
      poolIndex += 1
      courses.push({
        id: `${term.id}-${stage}-${i + 1}`,
        courseCode: seed.courseCode,
        title: seed.title,
        facultyName: seed.facultyName,
        stage,
      })
    }
  }
  return courses
}

export interface EvaluationPreviewCourse {
  id: string
  courseCode: string
  title: string
  facultyName: string
}

/** Course rows for a Do Next inspector. Count is the queue size, not a stage enum. */
export function previewCourses(seedKey: string, count: number): EvaluationPreviewCourse[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => {
    const seed = COURSE_POOL[index % COURSE_POOL.length]!
    return {
      id: `${seedKey}-${index + 1}`,
      courseCode: seed.courseCode,
      title: seed.title,
      facultyName: seed.facultyName,
    }
  })
}

/** Unique synced offerings for Confirm synced term. Count follows `courseOfferings`. */
export function syncedTermCourses(
  term: Pick<EvaluationTerm, "id" | "courseOfferings">,
): EvaluationPreviewCourse[] {
  return Array.from({ length: Math.max(0, term.courseOfferings) }, (_, index) => {
    const seed = COURSE_POOL[index % COURSE_POOL.length]!
    return {
      id: `${term.id}-synced-${index + 1}`,
      courseCode: `OT-${501 + index}`,
      title: seed.title,
      facultyName: seed.facultyName,
    }
  })
}
